import { useEffect, useRef } from "react";

export default function AmbientLayer() {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf;
    let W = 0;
    let H = 0;
    let flies = [];
    let sprinkles = [];
    let t = 0;

    const resize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const n = Math.max(5, Math.floor(W / 240));
      flies = Array.from({ length: n }, () => ({
        x: Math.random() * W,
        y: Math.random() * H,
        angle: Math.random() * Math.PI * 2,
        speed: Math.random() * 0.25 + 0.12,
        phase: Math.random() * Math.PI * 2,
        blink: Math.random() * 1.2 + 0.6,
        pr: Math.random() * 1.6 + 1.1,
      }));
    };
    resize();
    window.addEventListener("resize", resize);

    const drawSprinkle = (x, y, alpha, scale) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.scale(scale, scale);
      const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 10);
      g.addColorStop(0, `rgba(247,240,224,${alpha})`);
      g.addColorStop(1, "rgba(247,240,224,0)");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(0, 0, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = `rgba(255,252,244,${alpha})`;
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(-9, 0); ctx.lineTo(9, 0);
      ctx.moveTo(0, -9); ctx.lineTo(0, 9);
      ctx.stroke();
      ctx.restore();
    };

    const loop = () => {
      t += 0.016;
      ctx.clearRect(0, 0, W, H);

      flies.forEach((f) => {
        f.angle += (Math.random() - 0.5) * 0.35;
        f.x += Math.cos(f.angle) * f.speed;
        f.y += Math.sin(f.angle) * f.speed - 0.03;
        if (f.x < -10) f.x = W + 10;
        if (f.x > W + 10) f.x = -10;
        if (f.y < -10) f.y = H + 10;
        if (f.y > H + 10) f.y = -10;
        const glow = 0.35 + 0.65 * Math.abs(Math.sin(t * f.blink + f.phase));
        const rad = 14 * (0.7 + 0.5 * glow);
        const g = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, rad);
        g.addColorStop(0, `rgba(216,196,150,${0.26 * glow})`);
        g.addColorStop(1, "rgba(216,196,150,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(f.x, f.y, rad, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.pr * (0.6 + 0.35 * glow), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(250,244,228,${0.32 * glow})`;
        ctx.fill();
      });

      if (Math.random() < 0.018) {
        sprinkles.push({
          x: Math.random() * W,
          y: Math.random() * H * 0.9,
          life: 0,
          max: 0.9 + Math.random() * 0.7,
          size: 0.5 + Math.random() * 0.9,
        });
      }
      sprinkles = sprinkles.filter((s) => s.life < s.max);
      sprinkles.forEach((s) => {
        s.life += 0.016;
        const p = s.life / s.max;
        const alpha = Math.sin(p * Math.PI);
        const scale = s.size * (0.4 + 0.6 * Math.sin(p * Math.PI));
        drawSprinkle(s.x, s.y, alpha * 0.5, scale);
      });

      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="ambient-layer"
      aria-hidden
      className="fixed inset-0 pointer-events-none z-[57]"
      style={{ mixBlendMode: "screen" }}
    />
  );
}
